import clsx from 'clsx';
import { Link as GatsbyLink } from 'gatsby';
import type { CSSProperties, ComponentPropsWithRef, ReactNode, Ref } from 'react';
import { forwardRef, useContext } from 'react';
import { twMerge } from 'tailwind-merge';

import { LocaleContext } from './rebrand/Layout';

type Props = {
  activeClassName?: string;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  innerRef?: Ref<any>;
  localize?: boolean;
  target?: string;
  to?: string;
  style?: CSSProperties;
  onClick?: (e: any) => void;
} & Pick<ComponentPropsWithRef<'a'>, 'aria-label' | 'rel'>;

const Link = (
  {
    activeClassName,
    children,
    className,
    disabled,
    innerRef,
    localize = true,
    target,
    to = '',
    onClick,
    ...props
  }: Props,
  ref,
) => {
  let path = to;
  if (/www.koho.ca/.test(path)) {
    path = path.split('www.koho.ca')[1];
  }
  const internal = /^\/(?!\/)/.test(path);
  const mailTo = /^mailto/.test(path);
  const file = /\.[0-9a-z]+$/i.test(path);
  const ref2 = ref || innerRef;
  const locale = useContext(LocaleContext);

  // Use Gatsby Link for internal links, and <a> for others
  if (internal && !file) {
    return (
      <GatsbyLink
        onClick={(e) => {
          onClick && onClick(e);
          window?.scrollTo({ top: 0 });
        }}
        activeClassName={activeClassName}
        to={localize ? (locale === 'en' ? '' : '/fr') + path : path}
        ref={ref2}
        target={target}
        className={twMerge(clsx('hover:underline focus:underline active:underline', className))}
        style={props.style}
      >
        {children}
      </GatsbyLink>
    );
  } else if (mailTo) {
    return (
      <a href={to} onClick={onClick} target={target} {...props} ref={ref2}>
        {children}
      </a>
    );
  } else if (to?.length > 0 && to[0] === '#') {
    return (
      <button
        className={twMerge(
          clsx(
            'box-border inline-block cursor-pointer border-none bg-transparent p-0 text-blue-alt hover:underline focus:underline active:underline',
            className,
          ),
        )}
        onClick={(e) => {
          onClick && onClick(e);
          const el = document.getElementById(to);
          const y = el ? el.getBoundingClientRect().top + window.pageYOffset - 100 : 0;
          window.scrollTo({ top: y });
        }}
        disabled={disabled}
        {...props}
      >
        {children}
      </button>
    );
  } else {
    return (
      <a
        href={to}
        target={target ?? '_blank'}
        rel="noopener noreferrer"
        ref={ref2}
        className={twMerge(clsx('hover:underline focus:underline active:underline', className))}
        onClick={onClick}
        {...props}
      >
        {children}
      </a>
    );
  }
};

const ForwardRefLink = forwardRef(Link);

export default ForwardRefLink;
